/**
 * Validation and normalisation of the links students hand in.
 *
 * Pure and shared: the node card warns about an off-community link as you type, and the Worker
 * re-checks the same rules before writing anything. One implementation, no drift.
 *
 * The link is the whole proof of work in this product — Skool has no API, so "here is the post"
 * is the only evidence that exists (GDD §8.1). It has to be accepted generously enough that an
 * honest student never gets bounced, and normalised strictly enough that the same post cannot be
 * handed in twice under a different query string.
 */

export const MAX_URL_LENGTH = 600;

export type LinkProblem = 'prazno' | 'nije-link' | 'predugacko' | 'nije-web';

export interface LinkCheck {
  ok: boolean;
  problem?: LinkProblem;
  url?: string;
  /** True when the link points at the community itself, which is what we ask for. */
  community?: boolean;
}

const COMMUNITY_HOSTS = ['skool.com', 'www.skool.com'];

/** Tracking parameters carry no meaning and would let the same post pass twice. */
const STRIP_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'gclid',
  'igshid',
  'si',
  'ref',
  'ref_src',
];

export function checkLink(input: string): LinkCheck {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, problem: 'prazno' };
  if (trimmed.length > MAX_URL_LENGTH) return { ok: false, problem: 'predugacko' };

  // People paste "skool.com/..." without a scheme far more often than they paste a bad URL.
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return { ok: false, problem: 'nije-link' };
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
    return { ok: false, problem: 'nije-web' };
  if (!parsed.hostname.includes('.')) return { ok: false, problem: 'nije-link' };

  return {
    ok: true,
    url: normalise(parsed),
    community: COMMUNITY_HOSTS.includes(parsed.hostname.toLowerCase()),
  };
}

function normalise(url: URL): string {
  const out = new URL(url.toString());
  out.protocol = 'https:';
  // Only the case. The stored URL is what a mentor clicks, so it stays as close to what the
  // student pasted as possible — host equivalence is handled in canonicalForm, where it belongs.
  out.hostname = out.hostname.toLowerCase();
  out.hash = '';
  for (const param of STRIP_PARAMS) out.searchParams.delete(param);
  // Trailing slash on a path is noise; on the root it is canonical.
  if (out.pathname.length > 1 && out.pathname.endsWith('/'))
    out.pathname = out.pathname.replace(/\/+$/, '');
  out.search = out.searchParams.toString() ? `?${out.searchParams.toString()}` : '';
  return out.toString();
}

/**
 * The string a fingerprint is taken over. Kept here, next to the normaliser, because the two have
 * to agree — hashing happens on the server where the crypto lives.
 */
export function canonicalForm(url: string): string {
  const parsed = new URL(url);
  // The host is case-insensitive; the path is NOT. youtu.be/AbCdEf and youtu.be/abcdef are two
  // different videos, and folding their case would refuse the second as a duplicate.
  return `${parsed.hostname.toLowerCase().replace(/^www\./, '')}${parsed.pathname}${parsed.search}`;
}
